import { Injectable } from '@angular/core';
import { PersistedValueEnum } from '../../../../shared/models/persisted-value-enum';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { ConcourseApiService } from '../../../../shared/services/concourse-api.service';
import { PathConstants } from '../../constants/mro-path';
import { map, tap } from 'rxjs/operators';
import { CustomerEmpVNumberReqModel, TokenResModel } from '../../models/request-payload';
import { EmployeeProfile } from '../../models/employee-profile';
import { CustomerAdminApiService } from '../../../../shared/services/customer-admin-api.service';
import { AmplitudeService } from '../../../../shared/services/amplitude.service';

export interface UserDetails {
  id: number;
  email: string;
  employeeID: string;
  firstName: string;
  lastName: string;
  organizationId: number;
  division: string;
  phoneNumber: string;
  userName: string;
  userTypeId: number;
}

@Injectable()
export class UserAccessorService {
  public hasMultipleDivisions: boolean = localStorage.getItem('hasMultipleDivisions') === 'true' ? true : false;
  public subscriptions : Subscription[] = [];
  constructor(
    private concourseApi: ConcourseApiService,
    private customerConcourseApi: CustomerAdminApiService,
    private amplitudeService: AmplitudeService
  ) {}

  public getToken(): any {
    const ONE_HOUR_IN_MS = 3600000;
    let tokenData = {
      token: sessionStorage.getItem('token'),
      timestamp: sessionStorage.getItem('tokenExpiration'),
      divNo: localStorage.getItem('divNo')
    };

    if (tokenData.token == null || tokenData.timestamp == null || tokenData.divNo == null) {
      return false
    }

    let currentTime: Date = new Date();
    let tokenTimestamp = new Date(tokenData.timestamp);
    if (currentTime > tokenTimestamp || tokenTimestamp.getTime() - currentTime.getTime() < ONE_HOUR_IN_MS) {
      return false;
    }
    return tokenData;
  }

  public generateAndSaveIdentityInfo(): Observable<string> {
    return combineLatest([this.fetchTokenInfo(), this.fetchUserRole()]).pipe(
      map(([tokenInfo, userRole]) => ({ tokenInfo, userRole })),
      map(({ tokenInfo }) => tokenInfo.token)
    );
  }

  public getCustomerEmpVNumber(
    data: CustomerEmpVNumberReqModel
  ): Observable<EmployeeProfile> {
    return this.customerConcourseApi.getCustomerEmployeeVNumber(PathConstants.customerEmpVNumber, data)
      .pipe(map(res => res || null));
  }

  public getEmployeeInfo() {
    return {
      // userId: localStorage.getItem('userId'),
      employeeNumber: localStorage.getItem('employeeNumber'),
      divNo: +localStorage.getItem('divNo'),
      divName: localStorage.getItem('divName'),
      divCount: +localStorage.getItem('divCount'),
      userRole: this.getUserRole(),
    };
  }
public fetchEmployeeProfile(params: {
  employeeNumber: string;
  divNo: number;
}): Observable<EmployeeProfile> {
  const { employeeNumber, divNo } = params;
  const tokenData = this.getToken();

  return this.concourseApi
    .getEmployeeProfile(PathConstants.employeeProfile, {
      employeeNumber,
      divNo,
      token: tokenData?.token,
    })
    .pipe(map(res => res || null));
}

  public getCustomerInfo() {
    return {
      userId: localStorage.getItem('userId'),
    };
  }

  public getUserRole() {
    return localStorage.getItem('userRole');
  }

  public getUserDetails(): UserDetails {
    const userDetails = JSON.parse(
      localStorage.getItem(PersistedValueEnum.userDetails)
    );
    if (userDetails?.id) {
      return userDetails;
    }
    throw new Error('Could not parse userDetails from localstorage');
  }

  public fetchTokenInfo(): Observable<TokenResModel> {
    const { employeeID } = this.getUserDetails();
    return this.concourseApi
      .getToken(PathConstants.getToken, {
        employeeId: employeeID,
        application: 'CONCOURSE',
        channel: 'Browser',
      })
      .pipe(
        map((res) => {
          if (!res) {
            throw new Error(
              `User not found when fetching token for employee ID ${employeeID}`
            );
          }
          let tokenData = {
            token: res.token,
            timestamp: Date.now()
          };
          localStorage.setItem('divNo', res.divNo.toString());
          sessionStorage.setItem('token', tokenData.token);
          sessionStorage.setItem('tokenExpiration', this.addHoursToDate(tokenData.timestamp, 8).toString());
          localStorage.setItem('employeeNumber', res.employeeNumber);
          localStorage.setItem('divName', res.divName);
          localStorage.setItem('divCount', String(res.divCount));
          this.fetchUserRole().subscribe(role => {
            localStorage.setItem('userRole', role);
            this.amplitudeService.setRole(role);
          })
          return res;
        })
      );
  }

  public fetchCustomerTokenInfo(employeeID): Observable<TokenResModel> {
    return this.concourseApi
      .getToken(PathConstants.getToken, {
        employeeId: employeeID,
        application: 'CONCOURSE',
        channel: 'Browser',
      })
      .pipe(
        map((res) => {
          if (!res) {
            throw new Error(
              `User not found when fetching token for employee ID ${employeeID}`
            );
          }
          return res;
        })
      );
  }

  public fetchUserRole(): Observable<string> {
    const userId = this.getUserDetails().id;
    return this.concourseApi.getUserRoleByUserId(userId).pipe(
      map((res) => {
        const role = res[0];
        return role?.normalizedName;
      })
    );
  }



  public addHoursToDate(date: number | string |Date, hours: number): Date {
  const newDate = new Date(date);
  newDate.setHours(newDate.getHours() + hours);
  return newDate;
}
}


